import { useTranslation } from "react-i18next";
import { useAuthState } from "@/zustand/AuthState";
import { useTypingState } from "@/zustand/TypingState";
import { ChatType } from "@/server/types/chat";
import { resolveTypingLabel } from "./resolveTypingLabel";

/**
 * Reactive typing label for a chat. Returns `null` when nothing should render.
 *
 * Subscribes to THIS chat's inner map only (the store replaces a chat's
 * inner-map reference whenever that chat's typing changes, so the row
 * re-renders solely on its own traffic, not on every other chat's keep-alives),
 * then derives the active (non-expired) typers by filtering the inner map inline
 * and resolves the localized label. Used by both the message-list typing row and
 * the left-column chat-list line.
 *
 * The inline filter is pure (no store mutation during render); expired entries
 * are hidden from the returned list here and physically removed by the interval
 * sweep.
 */
export const useTypingLabel = (
  chatId: string | null | undefined,
  chatType: ChatType,
): string | null => {
  const { t } = useTranslation();
  const selfUsername = useAuthState((state) => state.username);

  // Subscribe ONLY to this chat's inner map, not the whole outer map. The store
  // replaces a chat's inner-map reference whenever that chat's typing changes,
  // so this row re-renders only on its own typing traffic — not on every other
  // chat's keep-alives. With hundreds of loaded chats this keeps a single user
  // typing from re-rendering the entire sidebar.
  const inner = useTypingState((state) =>
    chatId ? state.typingByChat.get(chatId) : undefined,
  );

  if (!chatId || !inner || inner.size === 0) {
    return null;
  }

  // Read-only: derive active (non-expired) typers directly from the inner map.
  // Pure — no store mutation during render; expiry removal is the sweep's job.
  const now = Date.now();
  const typingUsers: string[] = [];
  for (const [username, expiresAt] of inner) {
    if (expiresAt > now) {
      typingUsers.push(username);
    }
  }

  return resolveTypingLabel(chatType, typingUsers, t, selfUsername);
};
